import { useState, useEffect, useRef, useMemo, useContext, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FormLayout, RadioButton, Select, TextField, Button, Box, Form, InlineStack, Text, InlineGrid, Autocomplete, Tag, BlockStack, Divider, InlineError, Badge, Banner } from '@shopify/polaris';
import CategoryCombobox from './CategoryCombobox';
import { DeleteIcon, DragHandleIcon, PlusCircleIcon } from '@shopify/polaris-icons';

import { useToast } from '#client/components/common/Toast/ToastContext';
import { multiProductFilterService } from '#client/services/multiProductFilter/multiProductFilterServices';
import { parseSavedRules, getField, fieldChangeRelation, buildQueryFragment, toApiRuleGroups, summarizeRuleGroups, mixedRequirementErrors } from '#client/utils/helpers';
import { MULTI_PRODUCT_FILTER_CONSTANTS } from '#client/components/features/MultiProductFilter/constants/multiProductFilterConstants';
import { useProductFilterActions, useMultiProductFilter, useProductFilterStore } from '#client/stores/productFilter/useProductFilterStore';
import { useProgramActions, useProgramStore } from '#client/stores/program/useProgramStore';
import { SessionContext } from '#client/contexts/SessionContext';
import { describeRequiredChains, nameChain } from '#shared/ruleGroups';

// Group names in a chain, bold and joined with "and"
const renderChain = (names) =>
    names.map((name, index) => (
        <Fragment key={index}>
            {index > 0 && ' and '}
            <Text as="span" fontWeight="semibold">
                {name}
            </Text>
        </Fragment>
    ));

/**
 * What the grouped rules mean for a shopper: which groups make a product eligible, and what
 * has to be bought with each voucher. Required chains are OR'd, so it never says "this group".
 * Warns about a required chain no product matches, since no voucher could then meet it.
 */
export function RuleGroupsSummary({ ruleGroups, emptyRequiredChains = [] }) {
    const { eligibleChains, requiredChains } = summarizeRuleGroups(ruleGroups);

    return (
        <Box borderColor="border" borderWidth="025" borderRadius="200" padding="400">
            <BlockStack gap="200">
                <Text variant="headingSm" as="h3">
                    What this program allows
                </Text>
                <Text as="p">
                    A product is eligible if it matches{' '}
                    {eligibleChains.map((names, index) => (
                        <Fragment key={index}>
                            {index > 0 && ' or '}
                            {eligibleChains.length > 1 && names.length > 1 ? <>({renderChain(names)})</> : renderChain(names)}
                        </Fragment>
                    ))}
                    .
                </Text>
                <Text as="p">
                    {requiredChains.length
                        ? `At least ${describeRequiredChains(requiredChains)}${requiredChains.length > 1 ? ',' : ''} must be bought with this voucher. After that, the remaining balance can be used on any eligible item, including on later visits.`
                        : 'No group is required, so the voucher can be used on any eligible item.'}
                </Text>
                {emptyRequiredChains.map((names) => (
                    <Banner key={names.join('|')} tone="warning">
                        <p>No product matches {nameChain(names)}, so no voucher can meet this requirement. Change the rules so a product matches every group, or join the groups with OR.</p>
                    </Banner>
                ))}
            </BlockStack>
        </Box>
    );
}

export default function MultiProductFilter({ storeType = 'program' }) {
    // Determine which store to use based on storeType prop
    const isProgram = storeType === 'program';

    // Zustand store hooks - use product filter store directly (works for both program and cycle count)
    const { mode, restrictToEligibleProducts, eligibleProductRules, errors, filters, ruleGroups, emptyRequiredChains, conditionType, isProductsLoading } = useMultiProductFilter();

    // Get actions - always call both hooks to satisfy React Hooks rules
    const programActions = useProgramActions();
    const productFilterActions = useProductFilterActions();

    // Select which actions to use based on storeType
    const { setConditionType, setFilters, setIsProductsLoading, setAllProducts, setEligibleProductRules, setRestrictToEligibleProducts } = isProgram ? programActions : productFilterActions;

    const { addToast } = useToast();
    const [selected, setSelected] = useState(conditionType);
    const [productOptions, setProductOptions] = useState(MULTI_PRODUCT_FILTER_CONSTANTS.BASE_PRODUCT_OPTIONS);
    const [metafieldValues, setMetafieldValues] = useState({});
    // Keyed by `${listKey}:${index}`, where listKey is 'flat' or a rule group's key
    const [multiInputValues, setMultiInputValues] = useState({});
    const [categoryNames, setCategoryNames] = useState({});
    // Grouped rules mode: search errors by group key, and the group being dragged
    const [groupErrors, setGroupErrors] = useState({});
    const [draggableGroupKey, setDraggableGroupKey] = useState(null);
    const [dragFromIndex, setDragFromIndex] = useState(null);

    const isGrouped = conditionType === 'groupedRules';
    const { setRuleGroups, setRuleGroupsPayload, setEmptyRequiredChains, setSearchedConditionType, setSearchedFilters } = productFilterActions;
    // Externally managed programs come from NetSuite and don't get grouped rules
    const externallyManaged = useProgramStore.use.externallyManaged();

    const filtersInitialized = useRef(false);

    const { currentUser, isCustomerAuthenticated } = useContext(SessionContext);
    const isCustomer = isCustomerAuthenticated || currentUser?.role === 'customer';

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            filtersInitialized.current = false;
        };
    }, []);

    // Memoized default values
    const defaultAttribute = useMemo(() => {
        return productOptions[0]?.value;
    }, [productOptions]);
    const defaultCondition = useMemo(() => {
        const relations = fieldChangeRelation(defaultAttribute);
        if (!relations.length) {
            console.warn(`No relations found for field: ${defaultAttribute}`);
            return null;
        }
        return relations[0].value;
    }, [defaultAttribute]);

    //Handle condition type changes
    useEffect(() => {
        // Sync local selected state with Zustand store's conditionType
        if (setSelected && selected !== conditionType) {
            setSelected(conditionType);
        }
    }, [conditionType]);

    // Initialize filters from saved rules or create mode
    useEffect(() => {
        if (mode === 'create' && !filtersInitialized.current) {
            setFilters([
                {
                    attribute: defaultAttribute,
                    condition: defaultCondition,
                    value: '',
                },
            ]);
            filtersInitialized.current = true;
            return;
        }

        if (eligibleProductRules === '') return;

        // A grouped program's rule string is only a display value; its rules load from its groups
        if (useProductFilterStore.getState().conditionType === 'groupedRules') return;

        if (eligibleProductRules && filtersInitialized.current) {
            try {
                const { filters: parsedFilters, condition } = parseSavedRules(eligibleProductRules, conditionType);
                setSelected(condition);

                // Extract category names from parsed filters to populate categoryNames state BEFORE setFilters
                const extractedCategoryNames = {};
                parsedFilters.forEach((filter) => {
                    // Check for category_id attribute and categoryName property
                    if (filter.attribute === 'category_id' && filter.categoryName) {
                        // Handle both single and multi-value filters
                        const values = Array.isArray(filter.value) ? filter.value : [filter.value];
                        const names = Array.isArray(filter.categoryName) ? filter.categoryName : [filter.categoryName];
                        values.forEach((val, idx) => {
                            if (names[idx]) {
                                extractedCategoryNames[val] = names[idx];
                            }
                        });
                    }
                });

                // Set category names first to ensure they're available when filters render
                if (Object.keys(extractedCategoryNames).length > 0) {
                    setCategoryNames((prev) => ({ ...prev, ...extractedCategoryNames }));
                }

                // Then set filters. They're the rules as last searched, so a save compares against them
                setFilters(parsedFilters);
                setSearchedFilters(parsedFilters);
            } catch (error) {
                addToast({
                    type: 'error',
                    message: 'Error parsing rules',
                });
            }
        }
    }, [mode, eligibleProductRules]);

    const {
        data: metafieldsResponse,
        isError: isMetafieldsError,
        error: metafieldsError,
    } = useQuery({
        queryKey: ['multi-product-metafields'],
        queryFn: () => multiProductFilterService.getMetafields(),
        enabled: !isCustomer,
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        if (isCustomer) return;
        if (!metafieldsResponse) return;

        if (!metafieldsResponse.data || !Array.isArray(metafieldsResponse.data)) {
            setMetafieldValues({});
            setProductOptions([...MULTI_PRODUCT_FILTER_CONSTANTS.BASE_PRODUCT_OPTIONS]);
            return;
        }

        const metafieldOptions = metafieldsResponse.data.map((metafield) => ({
            label: `Metafield: ${metafield.name}`,
            value: `metafields.${metafield.namespace}.${metafield.key}`,
            type: metafield.type || 'string',
        }));

        // Store metafield values for dropdowns
        const valueMap = {};
        metafieldsResponse.data.forEach((metafield) => {
            const key = `metafields.${metafield.namespace}.${metafield.key}`;
            valueMap[key] = metafield.values || [];
        });

        setMetafieldValues(valueMap);
        setProductOptions([...MULTI_PRODUCT_FILTER_CONSTANTS.BASE_PRODUCT_OPTIONS, ...metafieldOptions]);
    }, [metafieldsResponse, isCustomer]);

    useEffect(() => {
        if (!isMetafieldsError) return;
        const errorMessage = metafieldsError?.response?.data?.error?.message || metafieldsError?.message || 'Failed to load metafields. Some filter options may be unavailable.';
        addToast({
            type: 'error',
            message: errorMessage,
        });
    }, [isMetafieldsError, metafieldsError, addToast]);

    // Customer view: do not render (avoids /app/multi-product-filter calls)
    if (isCustomer) return null;

    // Get condition options based on attribute
    const getConditionOptions = (attribute) => {
        return fieldChangeRelation(attribute);
    };

    // Rule row handlers for one rule list: the flat list, or the rules of one group
    const ruleListHandlers = (rules, onChange, listKey) => ({
        // Handle attribute change and update condition options
        handleAttributeChange: (index, value) => {
            const newFilters = [...rules];
            newFilters[index] = {
                ...newFilters[index],
                attribute: value,
                condition: getConditionOptions(value)[0].value,
                value: '',
            };
            onChange(newFilters);
        },

        // Handlers copy the rule they change, since the last searched rules share the rule objects
        handleFilterChange: (index, field, value) => {
            const updated = [...rules];
            updated[index] = { ...rules[index] };
            if (field === 'condition') {
                updated[index].condition = value;
                updated[index].value = value === 'IS_NULL' ? '-' : '';
                setMultiInputValues((prev) => ({ ...prev, [`${listKey}:${index}`]: '' }));
            } else {
                updated[index][field] = value;
            }
            onChange(updated);
        },

        // Handle adding a value to a multi-select filter. A label is kept on the rule so grouped rules save category names.
        handleAddMultiValue: (index, value, label) => {
            const updated = [...rules];
            const currentValues = Array.isArray(updated[index].value) ? updated[index].value : updated[index].value ? [updated[index].value] : [];
            if (value && !currentValues.includes(value)) {
                updated[index] = { ...rules[index], value: [...currentValues, value] };
                if (label) updated[index].labels = { ...updated[index].labels, [value]: label };
                onChange(updated);
            }
        },

        // Handle removing a value from a multi-select filter
        handleRemoveMultiValue: (index, valueToRemove) => {
            const updated = [...rules];
            const currentValues = Array.isArray(updated[index].value) ? updated[index].value : [updated[index].value];
            updated[index] = { ...rules[index], value: currentValues.filter((v) => v !== valueToRemove) };
            onChange(updated);
        },

        handleRemoveFilter: (index) => {
            onChange(rules.filter((_, i) => i !== index));
        },
    });

    const newRule = () => ({
        attribute: defaultAttribute,
        condition: defaultCondition,
        value: '',
    });

    const handleAddFilter = () => {
        setFilters([...filters, newRule()]);
    };

    // Grouped rules wraps the current rule list in one group the first time it is chosen
    const handleConditionTypeChange = (value) => {
        if (value === 'groupedRules' && !ruleGroups.length) {
            setRuleGroups([
                {
                    key: crypto.randomUUID(),
                    name: '',
                    joiner: conditionType === 'anyConditions' ? 'ANY' : 'ALL',
                    joinWithPrevious: null,
                    isRequired: false,
                    // Copies, so editing the group leaves the All/Any list as it was
                    filters: filters.map((filter) => ({ ...filter, value: Array.isArray(filter.value) ? [...filter.value] : filter.value })),
                },
            ]);
        }
        setConditionType(value);
    };

    const updateGroup = (index, changes) => {
        setRuleGroups(ruleGroups.map((group, i) => (i === index ? { ...group, ...changes } : group)));
        // An edited group's search errors no longer apply
        setGroupErrors((prev) => {
            const next = { ...prev };
            delete next[ruleGroups[index].key];
            return next;
        });
    };

    const handleAddGroup = () => {
        setRuleGroups([...ruleGroups, { key: crypto.randomUUID(), name: '', joiner: 'ALL', joinWithPrevious: 'OR', isRequired: false, filters: [newRule()] }]);
    };

    // Deleting a group removes the joiner above it, or the joiner below it when it was the first group
    const handleRemoveGroup = (index) => {
        setRuleGroups(ruleGroups.filter((_, i) => i !== index).map((group, i) => (i === 0 ? { ...group, joinWithPrevious: null } : group)));
    };

    // Reordering moves the groups; each joiner stays in its position between groups
    const handleMoveGroup = (from, to) => {
        if (from === null || from === to || to < 0 || to >= ruleGroups.length) return;
        const joiners = ruleGroups.map((group) => group.joinWithPrevious);
        const moved = [...ruleGroups];
        moved.splice(to, 0, moved.splice(from, 1)[0]);
        setRuleGroups(moved.map((group, i) => ({ ...group, joinWithPrevious: joiners[i] })));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Only proceed if in filter mode
        if (!restrictToEligibleProducts) return;

        // The server rejects the same thing; the error is already shown on the groups
        const requirementError = isGrouped && Object.values(mixedRequirementErrors(ruleGroups))[0];
        if (requirementError) {
            addToast({ type: 'error', message: requirementError });
            return;
        }

        setIsProductsLoading(true);
        setGroupErrors({});

        try {
            // Normalize and validate filter values
            const trimmed = (isGrouped ? ruleGroups.flatMap((group) => group.filters) : filters).map((f) => ({
                ...f,
                value: Array.isArray(f.value) ? f.value : (f.value ?? '').trim(),
            }));
            const invalidIndex = trimmed.findIndex((f) => {
                // if value is empty and condition is not IS_NULL
                if (f.condition === 'IS_NULL') {
                    return false;
                }
                if (Array.isArray(f.value)) {
                    return f.value.length === 0;
                }
                return f.value === '';
            });
            if (invalidIndex !== -1) {
                addToast({ type: 'error', message: 'All filter values must be filled out.' });
                return;
            }

            // Convert UI filters to Shopify rules
            // If a filter has multiple values (array), create multiple rules joined with OR
            // Convert UI filters to grouped rules structure
            // Each filter becomes a group - multi-values within a group are ORed
            const filterGroups = trimmed.map((f) => {
                const values = Array.isArray(f.value) ? f.value : [f.condition === 'IS_NULL' ? '-' : f.value];
                return {
                    field: getField(f.attribute),
                    relation: f.condition,
                    values: values, // Array of values to OR within this group
                };
            });

            // Build display query string
            const joiner = selected === 'anyConditions' ? ' OR ' : ' AND ';

            // Build query parts with proper grouping
            const queryParts = filterGroups.map((group, idx) => {
                // For category_id filters, append category name using pipe format (id|Name)
                let values = group.values;
                if (trimmed[idx].attribute === 'category_id') {
                    values = group.values.map((val) => {
                        const name = categoryNames[val];
                        if (name) {
                            return `${val}|${name}`;
                        }
                        return val;
                    });
                }

                if (values.length > 1) {
                    // Multiple values in group - wrap in parentheses and OR them
                    const valueQueries = values.map((val) => buildQueryFragment({ field: group.field, relation: group.relation, value: val }));
                    return `(${valueQueries.join(' OR ')})`;
                }
                // Single value - no parentheses needed
                return buildQueryFragment({ field: group.field, relation: group.relation, value: values[0] });
            });

            const queryStr = queryParts.join(joiner);

            // Request payload with grouped filters, or with the program's rule groups in Grouped rules mode
            const ruleGroupsPayload = isGrouped ? toApiRuleGroups(ruleGroups) : null;
            const requestData = isGrouped
                ? { ruleGroups: ruleGroupsPayload }
                : {
                      filterGroups: filterGroups, // Send groups instead of flat rules
                      conditionType: selected,
                  };

            // Add pagination params for both cycle counts and programs
            requestData.page = 1;
            requestData.limit = 10;
            requestData.createSnapshot = true;
            requestData.returnVariants = !isProgram; // variants for cycle counts, products for programs

            // Execute search
            const response = await multiProductFilterService.searchProducts(requestData);
            // Handle both product and variant responses
            const results = response?.data?.variants || response?.data?.products;
            const isVariantResponse = !!response?.data?.variants;
            // Set before the empty-results check, so a required chain that leaves nothing to find still warns
            if (isGrouped) setEmptyRequiredChains(response?.data?.emptyRequiredChains ?? []);

            if (!Array.isArray(results)) {
                throw new Error('Invalid response format from server');
            }
            if (results.length === 0) {
                throw new Error('No products found');
            }

            // Update store with query and results. Grouped rules leave the rule string alone; the server writes a display value on save
            if (isGrouped) setRuleGroupsPayload(ruleGroupsPayload);
            else setEligibleProductRules(queryStr);
            // A save now matches this search
            setSearchedConditionType(conditionType);
            if (!isGrouped) setSearchedFilters(filters);
            setAllProducts(results);
            setFilters(filters);

            // Store pagination info and snapshot ID for both programs and cycle counts
            if (response?.data) {
                const { snapshotId, pagination } = response.data;
                if (snapshotId && productFilterActions.setSnapshotId) {
                    productFilterActions.setSnapshotId(snapshotId);
                }
                if (pagination && productFilterActions.setTotalProductCount) {
                    productFilterActions.setTotalProductCount(pagination.totalCount);
                    productFilterActions.setCurrentPage(pagination.page);
                }
            }

            const resultType = isVariantResponse ? 'variants' : 'products';
            const totalCount = response?.data?.pagination?.totalCount || results.length;

            addToast({ type: 'success', message: `Found ${totalCount} ${resultType} successfully` });
        } catch (error) {
            setAllProducts([]);
            // Rule group errors carry the group's position, so each is shown on its group
            const details = error.response?.data?.error?.details;
            if (isGrouped && Array.isArray(details)) {
                const byGroup = {};
                details.forEach(({ path, message }) => {
                    const groupKey = ruleGroups[path?.[1]]?.key;
                    if (groupKey) byGroup[groupKey] = [...(byGroup[groupKey] || []), message];
                });
                setGroupErrors(byGroup);
            }
            const groupMessage = isGrouped && (Array.isArray(details) ? details[0]?.message : details);
            const errorMessage = groupMessage || error.response?.data?.error?.message || error.response?.data?.error || error.message || 'An error occurred while fetching products';
            addToast({ type: 'error', message: errorMessage });
        } finally {
            setIsProductsLoading(false);
        }
    };

    // Rule rows with AND/OR separators for one rule list: the flat list, or the rules of one group
    const renderRules = (rules, onChange, separator, listKey) => {
        const { handleAttributeChange, handleFilterChange, handleAddMultiValue, handleRemoveMultiValue, handleRemoveFilter } = ruleListHandlers(rules, onChange, listKey);

        const filterMarkup = rules.map((filter, index) => {
            const selectedOption = productOptions.find((o) => o.value === (filter.attribute || ''));
            const selectedType = (selectedOption?.type || 'string').toLowerCase();
            const condition = filter.condition || defaultCondition;

            const isBoolean = selectedType === 'boolean';
            const isCategory = filter.attribute === 'category_id';
            const isMetafield = filter.attribute?.startsWith('metafields.');
            const isViewMode = mode === 'view';
            const isNull = condition === 'IS_NULL';

            // Multi-select logic: All fields support multi-select for multiple values
            const supportsMultiSelect = !isNull && !isBoolean;

            const metafieldOptions = isMetafield && metafieldValues[filter.attribute] ? metafieldValues[filter.attribute].map((val) => ({ label: val, value: val })) : [];

            const showConditionSelect = !isBoolean;
            const showRemoveButton = rules.length > 1 && !isViewMode;

            // Get current values as array
            const currentValues = Array.isArray(filter.value) ? filter.value : filter.value ? [filter.value] : [];

            // Determine which value input to render
            const getValueInput = () => {
                // If the condition is IS_NULL, no value input is needed
                if (isNull) {
                    return null;
                }

                // Category multi-select with CategoryCombobox
                if (isCategory && supportsMultiSelect) {
                    return (
                        <Box>
                            <CategoryCombobox
                                value=""
                                onChange={(categoryData) => {
                                    if (categoryData?.id) {
                                        // Store category name for display
                                        setCategoryNames((prev) => ({
                                            ...prev,
                                            [categoryData.id]: categoryData.name,
                                        }));
                                        handleAddMultiValue(index, categoryData.id, categoryData.name);
                                    }
                                }}
                                disabled={isViewMode}
                                placeholder="Search and select categories"
                            />
                            {currentValues.length > 0 && (
                                <Box paddingBlockStart="200">
                                    <InlineStack gap="100" blockAlign="center">
                                        {currentValues.map((val, valIndex) => (
                                            <span key={`category-${index}-${val}-${valIndex}`} style={{ paddingRight: '3px' }}>
                                                <span style={{ paddingRight: '5px' }}>
                                                    <Tag onRemove={() => handleRemoveMultiValue(index, val)} disabled={isViewMode}>
                                                        {filter.labels?.[val] || categoryNames[val] || val}
                                                    </Tag>
                                                </span>
                                                {valIndex < currentValues.length - 1 && (
                                                    <Text variant="bodySm" tone="subdued" as="span">
                                                        OR
                                                    </Text>
                                                )}
                                            </span>
                                        ))}
                                    </InlineStack>
                                </Box>
                            )}
                        </Box>
                    );
                }

                if (isBoolean) {
                    return (
                        <Select
                            label="Value"
                            labelHidden
                            options={[
                                { label: 'True', value: 'true' },
                                { label: 'False', value: 'false' },
                            ]}
                            onChange={(value) => handleFilterChange(index, 'value', value)}
                            value={filter.value || ''}
                            disabled={isViewMode}
                        />
                    );
                }

                // Multi-select for metafields with predefined options
                if (isMetafield && metafieldOptions.length > 0) {
                    return (
                        <Box>
                            <Autocomplete
                                allowMultiple
                                options={metafieldOptions.filter((opt) => !currentValues.includes(opt.value))}
                                selected={currentValues}
                                onSelect={(selected) => handleFilterChange(index, 'value', selected)}
                                textField={<Autocomplete.TextField label="Value" labelHidden value="" placeholder="Select values" autoComplete="off" disabled={isViewMode} />}
                                disabled={isViewMode}
                            />
                            {currentValues.length > 0 && (
                                <Box paddingBlockStart="200">
                                    <InlineStack gap="100" blockAlign="center">
                                        {currentValues.map((val, valIndex) => (
                                            <span key={`metafield-${index}-${val}-${valIndex}`} style={{ paddingRight: '3px' }}>
                                                <span style={{ paddingRight: '5px' }}>
                                                    <Tag onRemove={() => handleRemoveMultiValue(index, val)} disabled={isViewMode}>
                                                        {val}
                                                    </Tag>
                                                </span>
                                                {valIndex < currentValues.length - 1 && (
                                                    <Text variant="bodySm" tone="subdued" as="span">
                                                        OR
                                                    </Text>
                                                )}
                                            </span>
                                        ))}
                                    </InlineStack>
                                </Box>
                            )}
                        </Box>
                    );
                }

                // Multi-select text input for all fields that support it (when condition is EQUALS)
                if (supportsMultiSelect) {
                    const inputKey = `${listKey}:${index}`;
                    const inputValue = multiInputValues[inputKey] || '';

                    const commitMultiValue = () => {
                        if (isViewMode) return;
                        const trimmedValue = inputValue.trim();
                        if (!trimmedValue) return;
                        handleAddMultiValue(index, trimmedValue);
                        setMultiInputValues({ ...multiInputValues, [inputKey]: '' });
                    };

                    return (
                        <Box>
                            <div
                                onKeyDownCapture={(e) => {
                                    if (e.key !== 'Enter') return;
                                    commitMultiValue();
                                }}
                            >
                                <TextField
                                    label="Value"
                                    labelHidden
                                    value={inputValue}
                                    onChange={(value) => setMultiInputValues({ ...multiInputValues, [inputKey]: value })}
                                    placeholder="Type and press add"
                                    autoComplete="off"
                                    disabled={isViewMode}
                                    connectedRight={
                                        <Button onClick={commitMultiValue} disabled={!inputValue.trim() || isViewMode}>
                                            Add
                                        </Button>
                                    }
                                />
                            </div>
                            {currentValues.length > 0 && (
                                <Box paddingBlockStart="200">
                                    <InlineStack gap="100" blockAlign="center">
                                        {currentValues.map((val, valIndex) => (
                                            <span key={`selected-${index}-${val}-${valIndex}`} style={{ paddingRight: '3px' }}>
                                                <span style={{ paddingRight: '5px' }}>
                                                    <Tag onRemove={() => handleRemoveMultiValue(index, val)} disabled={isViewMode}>
                                                        {val}
                                                    </Tag>
                                                </span>
                                                {valIndex < currentValues.length - 1 && (
                                                    <Text variant="bodySm" tone="subdued" as="span">
                                                        OR
                                                    </Text>
                                                )}
                                            </span>
                                        ))}
                                    </InlineStack>
                                </Box>
                            )}
                        </Box>
                    );
                }

                // Default text field for standard attributes and metafields without options
                return (
                    <TextField
                        label="Value"
                        labelHidden
                        value={Array.isArray(filter.value) ? filter.value[0] || '' : filter.value || ''}
                        onChange={(value) => handleFilterChange(index, 'value', value)}
                        placeholder="Enter value"
                        autoComplete="off"
                        disabled={isViewMode}
                    />
                );
            };

            return (
                <FormLayout.Group key={index} gap="400">
                    <InlineGrid columns={3} gap="400" alignItems="start">
                        <Select label="Attribute" labelHidden options={productOptions} onChange={(value) => handleAttributeChange(index, value)} value={filter.attribute || ''} disabled={isViewMode} />

                        {showConditionSelect && (
                            <Select
                                label="Condition"
                                labelHidden
                                options={getConditionOptions(filter.attribute || '')}
                                onChange={(value) => handleFilterChange(index, 'condition', value)}
                                value={filter.condition || ''}
                                disabled={isViewMode}
                            />
                        )}

                        {getValueInput()}
                    </InlineGrid>

                    <Button disabled={!showRemoveButton} icon={DeleteIcon} onClick={() => handleRemoveFilter(index)} accessibilityLabel="Remove filter" />
                </FormLayout.Group>
            );
        });

        // Interleave filters with AND/OR text
        const filterMarkupWithSeparators = [];
        filterMarkup.forEach((filter, index) => {
            filterMarkupWithSeparators.push(filter);
            // Add AND/OR between filters (not after the last one)
            if (index < filterMarkup.length - 1) {
                filterMarkupWithSeparators.push(
                    <Box key={`separator-${index}`} paddingBlockEnd="200">
                        <Text variant="bodyLg" as="p" alignment="start" fontWeight="semibold">
                            {separator}
                        </Text>
                    </Box>
                );
            }
        });

        return filterMarkupWithSeparators;
    };

    const isViewMode = mode === 'view';
    const requirementErrors = isGrouped ? mixedRequirementErrors(ruleGroups) : {};

    // Grouped rules mode: one card per group, with the AND/OR joiner to the group above between cards
    const groupMarkup = ruleGroups.map((group, groupIndex) => {
        const groupLabel = group.name.trim() || `group ${groupIndex + 1}`;

        return (
            <Fragment key={group.key}>
                {groupIndex > 0 && (
                    <InlineStack gap="300" blockAlign="center" wrap={false}>
                        <Box width="100%">
                            <Divider />
                        </Box>
                        <Box minWidth="104px">
                            <Select
                                label={`Joiner between ${groupLabel} and the group above`}
                                labelHidden
                                options={[
                                    { label: 'OR', value: 'OR' },
                                    { label: 'AND', value: 'AND' },
                                ]}
                                value={group.joinWithPrevious}
                                onChange={(value) => updateGroup(groupIndex, { joinWithPrevious: value })}
                                disabled={isViewMode}
                            />
                        </Box>
                        <Box width="100%">
                            <Divider />
                        </Box>
                    </InlineStack>
                )}
                <div
                    draggable={draggableGroupKey === group.key}
                    onDragStart={() => setDragFromIndex(groupIndex)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => handleMoveGroup(dragFromIndex, groupIndex)}
                    // Pressing the handle without dragging must not leave the card draggable
                    onPointerUp={() => setDraggableGroupKey(null)}
                    onDragEnd={() => {
                        setDraggableGroupKey(null);
                        setDragFromIndex(null);
                    }}
                >
                    <Box background="bg-surface-secondary" borderColor="border" borderWidth="025" borderRadius="200" padding="400">
                        <BlockStack gap="300">
                            <InlineStack gap="200" blockAlign="center" align="space-between" wrap={false}>
                                <InlineStack gap="200" blockAlign="center" wrap={false}>
                                    {!isViewMode && (
                                        // The card is only draggable while its handle is pressed, so text in the inputs can still be selected
                                        <Button
                                            variant="plain"
                                            icon={DragHandleIcon}
                                            accessibilityLabel={`Move ${groupLabel}. Drag, or use the up and down arrow keys`}
                                            onPointerDown={() => setDraggableGroupKey(group.key)}
                                            onKeyDown={(event) => {
                                                if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
                                                event.preventDefault();
                                                handleMoveGroup(groupIndex, groupIndex + (event.key === 'ArrowUp' ? -1 : 1));
                                            }}
                                        />
                                    )}
                                    <Box width="260px">
                                        <TextField
                                            label="Group name"
                                            labelHidden
                                            placeholder="Name this group"
                                            value={group.name}
                                            onChange={(name) => updateGroup(groupIndex, { name })}
                                            autoComplete="off"
                                            disabled={isViewMode}
                                        />
                                    </Box>
                                    <Badge tone={group.isRequired ? 'info' : undefined}>{group.isRequired ? 'Required' : 'Optional'}</Badge>
                                </InlineStack>
                                <InlineStack gap="200" blockAlign="center" wrap={false}>
                                    <Box minWidth="150px">
                                        <Select
                                            label={`Whether ${groupLabel} is required`}
                                            labelHidden
                                            options={[
                                                { label: 'Optional', value: 'optional' },
                                                { label: 'Required', value: 'required' },
                                            ]}
                                            value={group.isRequired ? 'required' : 'optional'}
                                            onChange={(value) => updateGroup(groupIndex, { isRequired: value === 'required' })}
                                            disabled={isViewMode}
                                        />
                                    </Box>
                                    {!isViewMode && (
                                        <Button icon={DeleteIcon} onClick={() => handleRemoveGroup(groupIndex)} disabled={ruleGroups.length === 1} accessibilityLabel={`Delete ${groupLabel}`} />
                                    )}
                                </InlineStack>
                            </InlineStack>
                            <InlineStack gap="200" blockAlign="center">
                                <Text variant="bodyMd" as="span">
                                    Rules in this group must match:
                                </Text>
                                <RadioButton
                                    label="All"
                                    name={`rule-group-joiner-${group.key}`}
                                    checked={group.joiner === 'ALL'}
                                    onChange={() => updateGroup(groupIndex, { joiner: 'ALL' })}
                                    disabled={isViewMode}
                                />
                                <RadioButton
                                    label="Any"
                                    name={`rule-group-joiner-${group.key}`}
                                    checked={group.joiner === 'ANY'}
                                    onChange={() => updateGroup(groupIndex, { joiner: 'ANY' })}
                                    disabled={isViewMode}
                                />
                            </InlineStack>
                            <FormLayout>
                                {renderRules(group.filters, (groupFilters) => updateGroup(groupIndex, { filters: groupFilters }), group.joiner === 'ANY' ? 'OR' : 'AND', group.key)}
                            </FormLayout>
                            {requirementErrors[group.key] && <InlineError message={requirementErrors[group.key]} fieldID={`rule-group-${group.key}`} />}
                            {groupErrors[group.key]
                                ?.filter((message) => message !== requirementErrors[group.key])
                                .map((message) => (
                                    <InlineError key={message} message={message} fieldID={`rule-group-${group.key}`} />
                                ))}
                            {!isViewMode && (
                                <Box>
                                    <Button icon={PlusCircleIcon} onClick={() => updateGroup(groupIndex, { filters: [...group.filters, newRule()] })}>
                                        Add another rule
                                    </Button>
                                </Box>
                            )}
                        </BlockStack>
                    </Box>
                </div>
            </Fragment>
        );
    });

    return (
        <Form onSubmit={handleSubmit}>
            <Box paddingBlockEnd="400">
                {isProgram && (
                    <InlineStack wrap={false} gap="200" blockAlign="center">
                        <Text variant="bodyMd">Applies to:</Text>
                        <RadioButton label="All Items" checked={!restrictToEligibleProducts} onChange={() => setRestrictToEligibleProducts(false)} disabled={mode === 'view'} />
                        <RadioButton label="Specific items" checked={restrictToEligibleProducts} onChange={() => setRestrictToEligibleProducts(true)} disabled={mode === 'view'} />
                    </InlineStack>
                )}
                {restrictToEligibleProducts && (
                    <InlineStack wrap={false} gap="200" blockAlign="center">
                        <Text variant="bodyMd">Products must match:</Text>
                        <RadioButton label="All Conditions" checked={conditionType === 'allConditions'} onChange={() => handleConditionTypeChange('allConditions')} disabled={mode === 'view'} />
                        <RadioButton label="Any Conditions" checked={conditionType === 'anyConditions'} onChange={() => handleConditionTypeChange('anyConditions')} disabled={mode === 'view'} />
                        {/* Programs only, and not externally managed ones; cycle counts share this component */}
                        {isProgram && !externallyManaged && (
                            <RadioButton label="Grouped rules" checked={isGrouped} onChange={() => handleConditionTypeChange('groupedRules')} disabled={mode === 'view'} />
                        )}
                    </InlineStack>
                )}
            </Box>
            {restrictToEligibleProducts && (
                <FormLayout>
                    {isGrouped ? <BlockStack gap="300">{groupMarkup}</BlockStack> : filters.length > 0 && renderRules(filters, setFilters, conditionType === 'allConditions' ? 'AND' : 'OR', 'flat')}
                    {errors.eligibleProductRules && <div style={{ marginTop: 4 }}>{errors.eligibleProductRules}</div>}
                    {mode !== 'view' &&
                        (isGrouped ? (
                            <Box>
                                <Button onClick={handleAddGroup} icon={PlusCircleIcon}>
                                    Add group
                                </Button>
                            </Box>
                        ) : (
                            <Button onClick={handleAddFilter} icon={PlusCircleIcon}>
                                Add another rule
                            </Button>
                        ))}
                    {isGrouped && ruleGroups.length > 0 && <RuleGroupsSummary ruleGroups={ruleGroups} emptyRequiredChains={emptyRequiredChains} />}

                    {mode !== 'view' && (
                        <Box paddingBlockStart="400">
                            <Button submit primary loading={isProductsLoading}>
                                Search Products
                            </Button>
                        </Box>
                    )}
                </FormLayout>
            )}
        </Form>
    );
}
